/** Sales-Aktionen: Verwaltung zeitlich befristeter Aktionspreise (Schreiben nur Admin).
 *  Die Preisermittlung selbst liegt ausschließlich im Backend (`pricing.py`). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SalesPromotionCalendar from "@/components/b2b/SalesPromotionCalendar";
import { toast } from "sonner";
import { Plus, Tag, Archive, Trash2, Pencil, Power, X } from "lucide-react";

const EMPTY = {
  name: "", internal_code: "", description: "", active: true,
  valid_from: "", valid_until: "", applies_to_b2c: true, applicable_price_list_ids: [],
  applies_to_all: false, product_ids: [], variant_ids: [], bundle_ids: [],
  discount_type: "percent", discount_value: "",
};

const StatusPill = ({ status, label, testId }) => {
  const cls = { aktiv: "bg-primary/15 text-primary", geplant: "bg-chart-4/20 text-chart-4",
    abgelaufen: "bg-muted text-muted-foreground", deaktiviert: "bg-destructive/10 text-destructive" }[status]
    || "bg-muted text-muted-foreground";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={testId}>{label}</span>;
};

export default function SalesPromotionsTab({ canEdit }) {
  const { t, fmtDate } = useI18n();
  const [rows, setRows] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [variants, setVariants] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [previewRef, setPreviewRef] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback((includeArchived = showArchived) => {
    api.get("/sales-promotions", { params: includeArchived ? { include_archived: true } : {} })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);
  useEffect(() => {
    load();
    api.get("/price-lists").then((r) => setPriceLists(r.data)).catch(() => {});
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de")))).catch(() => {});
    api.get("/bundles").then((r) => setBundles([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
  }, [load]);

  const variantOptions = useMemo(() => variants.map((v) => ({ value: v.id, label: `${v.product_name} · ${v.sku}` })), [variants]);
  const bundleOptions = useMemo(() => bundles.map((b) => ({ value: b.id, label: `${b.name} · ${b.sku}` })), [bundles]);
  const labelOf = (list, id) => (list.find((x) => x.value === id)?.label) || id;

  const startNew = () => { setEditId(null); setF(EMPTY); setPreview(null); setPreviewRef(""); setOpen(true); };
  const startEdit = (p) => {
    setEditId(p.id);
    setF({ ...EMPTY, ...p, valid_from: (p.valid_from || "").slice(0, 10), valid_until: (p.valid_until || "").slice(0, 10),
      discount_value: String(p.discount_value ?? "") });
    setPreview(null); setPreviewRef(""); setOpen(true);
  };
  const toggleIn = (key, id) => setF((p) => ({ ...p,
    [key]: (p[key] || []).includes(id) ? p[key].filter((x) => x !== id) : [...(p[key] || []), id] }));

  const save = async () => {
    const body = { ...f, discount_value: Number(f.discount_value) || 0 };
    try {
      if (editId) await api.put(`/sales-promotions/${editId}`, body);
      else await api.post("/sales-promotions", body);
      toast.success(t("sp.saved")); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const act = async (p, path) => {
    try { await api.post(`/sales-promotions/${p.id}/${path}`); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (p) => {
    if (!window.confirm(t("sp.archiveConfirm", { name: p.name }))) return;
    await act(p, "archive");
  };
  const remove = async (p) => {
    if (!window.confirm(t("sp.deleteConfirm", { name: p.name }))) return;
    try { await api.delete(`/sales-promotions/${p.id}`); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runPreview = async (vid) => {
    setPreviewRef(vid);
    if (!vid) { setPreview(null); return; }
    try {
      const plid = (f.applicable_price_list_ids || [])[0];
      const r = await api.get("/sales-promotions/preview", { params: { variant_id: vid, price_list_id: plid || undefined } });
      setPreview(r.data);
    } catch (e) { setPreview(null); toast.error(apiError(e)); }
  };

  const valueLabel = (p) => p.discount_type === "percent"
    ? `−${p.discount_value} %`
    : `${Number(p.discount_value).toFixed(2).replace(".", ",")} ${p.currency || "EUR"}`;
  const scopeLabel = (p) => [p.applies_to_b2c ? t("sp.b2c") : null,
    (p.price_list_names || []).length ? `${t("sp.priceLists")}: ${p.price_list_names.join(", ")}` : null,
    p.applies_to_all ? t("sp.allItems") : null].filter(Boolean).join(" · ") || "—";

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground max-w-2xl">{t("sp.subtitle")}</p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="sp-show-archived" />
            {t("sp.showArchived")}
          </label>
          {canEdit && <Button data-testid="new-sales-promotion-btn" onClick={startNew}><Plus className="h-4 w-4 mr-1.5" /> {t("sp.new")}</Button>}
        </div>
      </div>
      <p className="mb-4 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="sp-no-stacking-hint">{t("sp.noStacking")}</p>

      <Tabs defaultValue="list">
        <TabsList className="flex-wrap h-auto w-fit mb-4">
          <TabsTrigger value="list" data-testid="sp-tab-list">{t("spc.tabList")}</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="sp-tab-calendar">{t("spc.tabCalendar")}</TabsTrigger>
        </TabsList>
        <TabsContent value="list">
      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="sales-promotions-empty" icon={Tag} title={t("sp.emptyTitle")} hint={t("sp.emptyHint")} /> : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="sales-promotions-list">
            {rows.map((p) => (
              <div key={p.id} className="rounded-md border border-border bg-card p-4 shadow-sm" data-testid={`sales-promotion-${p.internal_code}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-medium truncate">{p.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{p.internal_code}</div>
                  </div>
                  <StatusPill status={p.status} label={t(`sp.status.${p.status}`, p.status)} testId={`sp-status-${p.internal_code}`} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <div>{t("sp.period")}: <span className="text-foreground">{fmtDate(p.valid_from)} – {fmtDate(p.valid_until)}</span></div>
                  <div>{t("sp.type")}: <span className="text-foreground">{p.discount_type === "percent" ? t("sp.typePercent") : t("sp.typeFixed")}</span></div>
                  <div>{t("sp.value")}: <span className="font-mono text-foreground">{valueLabel(p)}</span></div>
                  <div>{t("sp.items")}: <span className="text-foreground">{p.applies_to_all ? t("sp.allItems") : p.item_count}</span></div>
                  <div className="col-span-2">{t("sp.scope")}: <span className="text-foreground">{scopeLabel(p)}</span></div>
                  {p.used_in_orders > 0 && <div className="col-span-2 text-foreground">{t("sp.usedInOrders", { count: p.used_in_orders })}</div>}
                </div>
                {canEdit && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!p.archived && <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`sp-edit-${p.internal_code}`}><Pencil className="h-3.5 w-3.5 mr-1" /> {t("common.edit")}</Button>}
                    {!p.archived && <Button size="sm" variant="outline" onClick={() => act(p, p.active ? "deactivate" : "activate")} data-testid={`sp-toggle-${p.internal_code}`}><Power className="h-3.5 w-3.5 mr-1" /> {p.active ? t("sp.deactivate") : t("sp.activate")}</Button>}
                    {!p.archived && <Button size="sm" variant="ghost" onClick={() => archive(p)} data-testid={`sp-archive-${p.internal_code}`}><Archive className="h-3.5 w-3.5 mr-1" /> {t("sp.archive")}</Button>}
                    {p.archived && <span className="self-center text-xs text-muted-foreground" data-testid={`sp-archived-${p.internal_code}`}>{t("sp.archivedFlag")}</span>}
                    {p.used_in_orders === 0 && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p)} data-testid={`sp-delete-${p.internal_code}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </TabsContent>
        <TabsContent value="calendar">
          <SalesPromotionCalendar rows={rows || []} onEdit={canEdit ? startEdit : () => {}} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? t("sp.edit") : t("sp.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("common.name")}</Label><Input className="mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sp-name" /></div>
            <div><Label>{t("sp.code")}</Label><Input className="mt-1.5" value={f.internal_code} onChange={(e) => setF({ ...f, internal_code: e.target.value })} data-testid="sp-code" /></div>
            <div className="col-span-2"><Label>{t("sp.description")}</Label><Textarea className="mt-1.5" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sp-description" /></div>
            <div><Label>{t("sp.validFrom")}</Label><Input type="date" className="mt-1.5" value={f.valid_from} onChange={(e) => setF({ ...f, valid_from: e.target.value })} data-testid="sp-valid-from" /></div>
            <div><Label>{t("sp.validUntil")}</Label><Input type="date" className="mt-1.5" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} data-testid="sp-valid-until" /></div>
            <div><Label>{t("sp.type")}</Label>
              <Select value={f.discount_type} onValueChange={(v) => setF({ ...f, discount_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="sp-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">{t("sp.typePercent")}</SelectItem><SelectItem value="fixed">{t("sp.typeFixed")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t("sp.value")}</Label><Input type="number" step="0.01" min="0" className="mt-1.5" value={f.discount_value} onChange={(e) => setF({ ...f, discount_value: e.target.value })} data-testid="sp-value" /></div>

            <div className="col-span-2 rounded-md border border-border p-3 space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("sp.scope")}</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!f.applies_to_b2c} onChange={(e) => setF({ ...f, applies_to_b2c: e.target.checked })} data-testid="sp-b2c" />
                {t("sp.b2c")}
              </label>
              <div>
                <div className="text-sm mb-1">{t("sp.priceLists")}</div>
                <div className="flex flex-wrap gap-2">
                  {priceLists.map((pl) => {
                    const on = (f.applicable_price_list_ids || []).includes(pl.id);
                    return (
                      <button type="button" key={pl.id} onClick={() => toggleIn("applicable_price_list_ids", pl.id)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
                        data-testid={`sp-pl-${pl.id}`}>{pl.name}</button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-span-2 rounded-md border border-border p-3 space-y-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("sp.items")}</div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!f.applies_to_all} onChange={(e) => setF({ ...f, applies_to_all: e.target.checked })} data-testid="sp-all-items" />
                {t("sp.allItems")}
              </label>
              <div>
                <div className="text-sm mb-1">{t("sp.variants")}</div>
                <SearchSelect testId="sp-variant-picker" value="" placeholder={t("sp.selectItems")}
                  options={variantOptions.filter((o) => !(f.variant_ids || []).includes(o.value))}
                  onChange={(v) => v && toggleIn("variant_ids", v)} />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(f.variant_ids || []).map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs" data-testid={`sp-variant-chip-${id}`}>
                      {labelOf(variantOptions, id)}
                      <button type="button" onClick={() => toggleIn("variant_ids", id)}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm mb-1">{t("sp.bundles")}</div>
                <SearchSelect testId="sp-bundle-picker" value="" placeholder={t("sp.selectItems")}
                  options={bundleOptions.filter((o) => !(f.bundle_ids || []).includes(o.value))}
                  onChange={(v) => v && toggleIn("bundle_ids", v)} />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(f.bundle_ids || []).map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs" data-testid={`sp-bundle-chip-${id}`}>
                      {labelOf(bundleOptions, id)}
                      <button type="button" onClick={() => toggleIn("bundle_ids", id)}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-span-2 rounded-md border border-dashed border-border p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">{t("sp.preview")}</div>
              <SearchSelect testId="sp-preview-picker" value={previewRef} placeholder={t("sp.selectItems")}
                options={variantOptions} onChange={runPreview} />
              {preview && (
                <div className="mt-2 space-y-1 text-xs" data-testid="sp-preview-result">
                  <div>{t("sp.b2c")}: {t("sp.regularPrice")} <span className="font-mono text-foreground">{eur(preview.b2c?.base_unit_price)}</span>
                    {" → "}<span className="font-mono text-foreground">{eur(preview.b2c?.unit_price)}</span>
                    {" · "}{t(`pv.src.${preview.b2c?.pricing_source}`, preview.b2c?.pricing_source)}</div>
                  {preview.b2b && (
                    <div>{t("sp.priceLists")}: <span className="font-mono text-foreground">{eur(preview.b2b?.unit_price)}</span>
                      {" · "}{t(`pv.src.${preview.b2b?.pricing_source}`, preview.b2b?.pricing_source)}</div>
                  )}
                  {preview.b2b_hint && <div className="text-muted-foreground">{preview.b2b_hint}</div>}
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">{editId ? t("sp.previewHint") : t("sp.previewDraft")}</p>
            </div>

            <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="sp-active" />
              {t("common.active")}
            </label>
          </div>
          <DialogFooter><Button onClick={save} data-testid="sp-save-btn">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
